/**
 * Micrographics node-pill system (brand spec "Micrographics").
 *
 * Every pill is dimensioned from X — the cap height of its own label, which
 * for DM Sans is ≈ 0.72 em. Geometry therefore lives in `node-pill.css` as
 * `calc()` expressions over `--node-x` and scales with `font-size` alone;
 * the `size` variant here only sets that font-size.
 *
 * The brand's "flat fill + 1 px outline" rule means every *filled* surface
 * also carries a 1 px foreground stroke — both because the identity asks for
 * it and because WCAG 1.4.11 needs it: the brand blue on the light beige
 * ground is only ~1.3:1 at the edge, so the fill alone cannot delimit the
 * control.
 */
import { cva } from "class-variance-authority"

/** Cap height of DM Sans as a fraction of the em box. */
export const NODE_X_EM = 0.72

/** Default connector line length, in X units. */
export const NODE_CONNECTOR_LENGTH_X = 3

/** Default `IconContainer` edge, in px (64 px box ↔ 20 px padding ↔ 24 px icon). */
export const ICON_CONTAINER_SIZE_PX = 64

export type NodePillKind = "marker" | "label" | "flow"
export type NodePillTone = "outlined" | "primary" | "secondary"
export type NodePillSize = "sm" | "md" | "lg"
export type NodePillConnector = "none" | "left" | "right" | "both"

/** Font-size step per size token — shared by the pill and its connector group. */
export const NODE_PILL_SIZE_CLASS: Record<NodePillSize, string> = {
  sm: "text-xs",
  md: "text-sm",
  lg: "text-base",
}

/** What a single side of a pill renders: the node circle, the line, or both. */
export type NodePillSide = {
  node: boolean
  line: boolean
}

const NO_SIDE: NodePillSide = { node: false, line: false }

/**
 * Resolve which connector parts each side renders.
 *
 * - `label` never carries a connector.
 * - `marker` always carries its node circle; with `connector: "none"` the
 *   circle sits flush against the pill (no 3X line), otherwise it attaches
 *   through the line on the requested side(s).
 * - `flow` only draws a connector where one is requested — the pills in a
 *   flow are joined by the line between them.
 */
export function resolveConnectorSides(
  kind: NodePillKind,
  connector: NodePillConnector
): { left: NodePillSide; right: NodePillSide } {
  if (kind === "label") {
    return { left: NO_SIDE, right: NO_SIDE }
  }

  const wantsLeft = connector === "left" || connector === "both"
  const wantsRight = connector === "right" || connector === "both"

  if (kind === "marker" && !wantsLeft && !wantsRight) {
    // A marker is defined by its node; with no side requested it hangs on the
    // reading-order side, flush against the pill.
    return { left: { node: true, line: false }, right: NO_SIDE }
  }

  return {
    left: wantsLeft ? { node: true, line: true } : NO_SIDE,
    right: wantsRight ? { node: true, line: true } : NO_SIDE,
  }
}

export const nodePillVariants = cva(
  // Geometry, radius, stroke width and the guiding transition come from
  // `node-pill.css`; only palette and type live here.
  "node-pill",
  {
    variants: {
      kind: {
        marker: "node-pill--marker",
        label: "node-pill--label",
        flow: "node-pill--flow",
      },
      tone: {
        outlined: "border-foreground bg-transparent text-foreground",
        primary: "border-foreground bg-primary text-primary-foreground",
        secondary: "border-foreground bg-secondary text-secondary-foreground",
      },
      size: NODE_PILL_SIZE_CLASS,
      /** Brand tag (ABC Solar Ultra, uppercase) vs. functional tag (DM Sans). */
      brand: {
        true: "type-headline",
        false: "font-sans font-medium",
      },
      active: {
        true: "node-pill--active",
        false: "",
      },
    },
    compoundVariants: [
      {
        kind: "flow",
        active: true,
        // Active step of a flow highlights blue. Text on blue is always
        // charcoal (`--primary-foreground`), and the 1 px outline stays.
        className: "border-foreground bg-primary text-primary-foreground",
      },
    ],
    defaultVariants: {
      kind: "label",
      tone: "outlined",
      size: "md",
      brand: false,
      active: false,
    },
  }
)
